import React from 'react'
import { Button } from '@/components/ui/button'

const RELOAD_GUARD_KEY = 'route-chunk-reloaded-at'
const RELOAD_GUARD_MS = 10_000

/**
 * A dynamic import fails when the browser asks for a chunk filename that no
 * longer exists on the server — which happens to every open tab the moment a
 * new build is deployed. Reloading fetches the new index.html and its new
 * chunk names, so it fixes that case outright.
 *
 * We record when we last auto-reloaded. A second failure within RELOAD_GUARD_MS
 * means the reload did not help, so we stop and show a manual retry rather than
 * trapping the user in a refresh loop. A failure long after that is a fresh
 * deploy, not a loop, so it is allowed to auto-reload again.
 */
const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error)
  return /ChunkLoadError|Loading chunk \d+ failed|dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
    message,
  )
}

interface RouteChunkBoundaryState {
  hasError: boolean
}

/**
 * Catches failures from the lazily-loaded route chunks in AppRouter so a bad
 * fetch degrades to a retry prompt instead of a blank screen.
 */
class RouteChunkBoundary extends React.Component<
  { children: React.ReactNode },
  RouteChunkBoundaryState
> {
  state: RouteChunkBoundaryState = { hasError: false }

  static getDerivedStateFromError(): RouteChunkBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: unknown): void {
    if (!isChunkLoadError(error)) {
      console.error('[RouteChunkBoundary] Route failed to render:', error)
      return
    }

    let justReloaded = false
    try {
      const lastReloadedAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0)
      justReloaded = Date.now() - lastReloadedAt < RELOAD_GUARD_MS
      if (!justReloaded) sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
    } catch {
      // Private browsing or blocked storage — fall through to the manual retry.
    }

    if (!justReloaded) window.location.reload()
  }

  private handleRetry = (): void => {
    try {
      sessionStorage.removeItem(RELOAD_GUARD_KEY)
    } catch {
      // Nothing to clear; the reload below is what matters.
    }
    window.location.reload()
  }

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-foreground">This page failed to load</h2>
          <p className="text-sm text-muted-foreground">
            The app may have been updated since you opened this tab. Reloading usually fixes it.
          </p>
        </div>
        <Button onClick={this.handleRetry}>Reload</Button>
      </div>
    )
  }
}

export default RouteChunkBoundary
